export interface ScopedPendingMessage {
  localChatId: string | null;
  persistedMessageId?: string;
}

export interface OptimisticAttachmentPreview {
  imageUrl?: string;
  imageUrls?: string[];
  audioUrl?: string;
}

export function getRetryImageFields(message: OptimisticAttachmentPreview, urls: string[]) {
  return !message.imageUrls?.length && urls.length === 1 && message.imageUrl === urls[0]
    ? { imageUrl: urls[0] }
    : { imageUrls: urls };
}

export function upsertOptimisticAttachmentPreview<T extends OptimisticAttachmentPreview>(
  previews: Readonly<Record<string, T>>,
  messageId: string,
  preview: T
) {
  return {
    ...previews,
    [messageId]: {
      ...previews[messageId],
      ...preview
    }
  };
}

export function mergeOptimisticAttachmentPreview<
  TMessage extends OptimisticAttachmentPreview,
  TPreview extends OptimisticAttachmentPreview
>(message: TMessage, preview?: TPreview) {
  if (!preview) return message;

  return {
    ...message,
    imageUrl: preview.imageUrl || message.imageUrl,
    ...(message.imageUrls?.length || preview.imageUrls?.length
      ? { imageUrls: message.imageUrls?.length ? message.imageUrls : preview.imageUrls }
      : {}),
    audioUrl: preview.audioUrl || message.audioUrl
  };
}

export function countLocalMessagesForChat<T extends { localChatId: string | null }>(
  messages: readonly T[],
  chatId: string | null
) {
  return messages.reduce((count, message) => (
    message.localChatId === chatId ? count + 1 : count
  ), 0);
}

export function shouldRenderPendingMessage(
  pendingMessage: ScopedPendingMessage | null,
  currentChatId: string | null,
  persistedMessageIds: ReadonlySet<string>
) {
  if (!pendingMessage || pendingMessage.localChatId !== currentChatId) return false;
  return !pendingMessage.persistedMessageId || !persistedMessageIds.has(pendingMessage.persistedMessageId);
}

export function isNewChatSurface(params: {
  currentChatId: string | null;
  persistedMessageCount: number;
  localOfflineMessageCount: number;
  hasPendingMessage: boolean;
}) {
  return params.persistedMessageCount === 0
    && params.localOfflineMessageCount === 0
    && !params.hasPendingMessage;
}

export function isTypingIndicatorScopedToChat(params: {
  currentChatId: string | null;
  typingChatId: string | null;
  hasPendingMessage: boolean;
  hasStartTime: boolean;
}) {
  if (!params.hasStartTime) return false;
  if (params.currentChatId) return params.typingChatId === params.currentChatId;
  return params.hasPendingMessage;
}

export function shouldShowTypingIndicator(params: {
  scopedToCurrentChat: boolean;
  hasStartTime: boolean;
  hasStreamingBotMessage: boolean;
  hasAnimatingBotMessage: boolean;
  hasTransientError: boolean;
  hasActiveDocumentGeneration: boolean;
}) {
  return params.scopedToCurrentChat
    && params.hasStartTime
    && !params.hasStreamingBotMessage
    && !params.hasAnimatingBotMessage
    && !params.hasTransientError
    && !params.hasActiveDocumentGeneration;
}
